/**
 * WebSocket transport for the nettacco browser client.
 *
 * Owns a single `WebSocket` for the lifetime of one channel session,
 * performs the `hello` / `hello_ok` handshake, and surfaces the post-
 * handshake protocol events to the caller via a small set of callbacks.
 *
 * Audio frames are passed in and out as raw `Uint8Array`s so this layer
 * stays codec-agnostic; the audio module is responsible for Opus
 * encode/decode and for synthesising the per-frame `sequence` and
 * `timestamp_ms` values.
 */

import {
    type AudioFrame,
    type ControlMessage,
    type FloorDeniedReason,
    type HelloOk,
    type HelloRejectReason,
    type MemberInfo,
    type TalkerInfo,
    decodeAudioFrame,
    encodeAudioFrame,
    parseControlMessage,
    serialiseControlMessage,
} from "./protocol";

/** Lifecycle state of the transport. */
export type ConnectionState =
    | "idle"
    | "connecting"
    | "handshaking"
    | "connected"
    | "reconnecting"
    | "disconnected";

/**
 * Listener callbacks supplied at construction. All are optional — the
 * caller installs only the events it cares about.
 */
export interface TransportListeners {
    /** Fires every time the connection state changes. */
    onStateChange?: (state: ConnectionState, reason?: string) => void;
    /** A new connection has acquired the floor. */
    onFloorTaken?: (talker: TalkerInfo) => void;
    /** A connection has released the floor (explicitly or by disconnect). */
    onFloorReleased?: (connectionId: string) => void;
    /** A new connection joined the channel roster. */
    onMemberJoined?: (member: MemberInfo) => void;
    /** A connection left the channel (clean close or abrupt disconnect). */
    onMemberLeft?: (connectionId: string) => void;
    /** This client's own `request_floor` was granted. */
    onFloorGranted?: () => void;
    /** This client's own `request_floor` was denied. */
    onFloorDenied?: (reason: FloorDeniedReason) => void;
    /** A peer's audio frame arrived (already decoded from the wire). */
    onAudio?: (frame: AudioFrame) => void;
    /**
     * After an unexpected disconnect mid-session the transport
     * automatically retries the handshake with backoff. This event
     * fires when one of those retries succeeds, supplying the *new*
     * `HelloOk` (which carries a fresh `connection_id` and an updated
     * `current_talkers` snapshot — the channel screen should refresh
     * its talker list and reset any local floor flags).
     */
    onReconnected?: (helloOk: HelloOk) => void;
    /** A protocol-level error reported by the server. */
    onServerError?: (code: string, message: string) => void;
    /** A transport-level failure that ended the session. */
    onTransportError?: (message: string) => void;
}

/**
 * Reason the handshake failed. Pairs with a human-readable message in the
 * thrown error.
 */
export type HandshakeFailure =
    | { kind: "rejected"; reason: HelloRejectReason }
    | { kind: "timeout" }
    | { kind: "transport"; detail: string }
    | { kind: "protocol"; detail: string };

/**
 * Error thrown by [`NettaccoTransport.connect`] when the handshake fails.
 */
export class HandshakeError extends Error {
    public override readonly name = "HandshakeError";
    constructor(public readonly failure: HandshakeFailure) {
        super(handshakeFailureMessage(failure));
    }
}

function handshakeFailureMessage(f: HandshakeFailure): string {
    switch (f.kind) {
        case "rejected":
            return `server rejected handshake: ${f.reason}`;
        case "timeout":
            return "handshake timed out";
        case "transport":
            return `transport error during handshake: ${f.detail}`;
        case "protocol":
            return `protocol error during handshake: ${f.detail}`;
    }
}

/** Maximum time to wait for `hello_ok` or `hello_reject` after sending `hello`. */
const HANDSHAKE_TIMEOUT_MS = 10_000;

/**
 * Backoff schedule for reconnect attempts after an unexpected close.
 * Roughly 13 s of total retry time, with the first retry happening
 * essentially immediately so a brief network blip is invisible to the
 * user. After this list is exhausted the transport gives up and
 * surfaces a normal disconnect to the caller.
 */
const RECONNECT_BACKOFF_MS = [200, 400, 800, 1600, 3200, 6400] as const;

/**
 * Single-channel WebSocket transport.
 *
 * Construct, register listeners, then `connect`. After `connect` resolves
 * with the [`HelloOk`], use the floor / audio methods. Always pair a
 * successful `connect` with an eventual `disconnect` to release the
 * underlying socket.
 */
export class NettaccoTransport {
    private ws: WebSocket | null = null;
    private state: ConnectionState = "idle";
    /** Filled after a successful handshake; `null` before. */
    private helloOk: HelloOk | null = null;
    private listeners: TransportListeners;
    /**
     * Credentials from the original `connect()` call. Retained so
     * unexpected disconnects can re-run the handshake without bouncing
     * the user back to the lobby.
     *
     * Holding the channel secret in memory for the lifetime of a
     * session is consistent with the deliberate per-session entry
     * design — it never reaches `localStorage` or any other persistent
     * store. When the user explicitly disconnects we drop it.
     */
    private credentials: { url: string; secret: string; displayName: string } | null = null;
    /** Pending reconnect timer id, or `null` when no retry is scheduled. */
    private reconnectTimer: number | null = null;
    /** Index into [`RECONNECT_BACKOFF_MS`] for the *next* retry. */
    private reconnectAttempt = 0;
    /** True while the user-facing `disconnect()` is in progress; suppresses reconnects. */
    private userDisconnecting = false;

    constructor(listeners: TransportListeners = {}) {
        this.listeners = listeners;
    }

    /**
     * Replace the listener bag. Returns the previous listeners so the
     * caller can restore them later. Useful when one screen takes over
     * for another while the same transport instance remains live.
     */
    public setListeners(next: TransportListeners): TransportListeners {
        const previous = this.listeners;
        this.listeners = next;
        return previous;
    }

    /** Current connection state. */
    public getState(): ConnectionState {
        return this.state;
    }

    /** Server-assigned connection id, available after a successful handshake. */
    public getConnectionId(): string | null {
        return this.helloOk?.connection_id ?? null;
    }

    /**
     * Open the WebSocket, perform the handshake, and resolve with the
     * server's [`HelloOk`] once accepted.
     *
     * On any failure (network, timeout, rejection) the underlying socket
     * is closed and a [`HandshakeError`] is thrown. The transport returns
     * to `disconnected` so the caller can construct a fresh instance and
     * try again.
     */
    public async connect(
        url: string,
        secret: string,
        displayName: string,
    ): Promise<HelloOk> {
        if (this.state !== "idle") {
            throw new Error(`cannot connect from state ${this.state}`);
        }
        this.setState("connecting");

        const ws = new WebSocket(url);
        // Binary frames carry audio — handle them as ArrayBuffer for
        // cheap zero-copy reads via DataView.
        ws.binaryType = "arraybuffer";
        this.ws = ws;

        try {
            await waitForOpen(ws);
        } catch (e) {
            this.cleanupAfterFailure();
            throw new HandshakeError({
                kind: "transport",
                detail: errorMessage(e),
            });
        }

        this.setState("handshaking");
        ws.send(
            serialiseControlMessage({
                type: "hello",
                display_name: displayName,
                secret: secret,
            }),
        );

        const helloOk = await this.awaitHandshakeReply(ws);
        this.helloOk = helloOk;
        // Stash the credentials so unexpected disconnects can re-run
        // the handshake transparently. Cleared only on user-initiated
        // disconnect.
        this.credentials = { url, secret, displayName };
        // Switch the steady-state listeners on before announcing connected,
        // so any frame already in the receive queue is handled correctly.
        this.attachSteadyStateListeners(ws);
        this.setState("connected");
        return helloOk;
    }

    /** Send a `request_floor`. Caller waits for `onFloorGranted` / `onFloorDenied`. */
    public requestFloor(): void {
        this.sendControl({ type: "request_floor" });
    }

    /** Send a `release_floor`. Idempotent server-side. */
    public releaseFloor(): void {
        this.sendControl({ type: "release_floor" });
    }

    /**
     * Encode and send an audio frame. The caller supplies the Opus
     * payload, sequence number, and timestamp; this method fills in the
     * connection id from the handshake reply.
     */
    public sendAudio(opusPayload: Uint8Array, sequence: number, timestampMs: number): void {
        if (this.state !== "connected" || this.ws === null || this.helloOk === null) {
            // Silently drop audio if not connected — capture may produce a
            // frame between disconnect and the audio worklet stopping.
            return;
        }
        const bytes = encodeAudioFrame({
            sender: this.helloOk.connection_id,
            sequence,
            timestamp_ms: timestampMs,
            opus_payload: opusPayload,
        });
        this.ws.send(bytes);
    }

    /**
     * Close the WebSocket and tear down listeners. Safe to call from any
     * state, including before `connect` or while a reconnect is pending.
     *
     * Sets a flag so any in-flight reconnect attempt knows to abandon
     * itself rather than bouncing the user back to a fresh session.
     */
    public disconnect(reason?: string): void {
        this.userDisconnecting = true;
        this.cancelReconnect();
        if (this.ws !== null) {
            try {
                this.ws.close(1000, reason ?? "client disconnect");
            } catch {
                // close() can throw if the socket is already closing;
                // we don't care, we're tearing down anyway.
            }
            this.ws = null;
        }
        this.helloOk = null;
        this.credentials = null;
        if (this.state !== "disconnected") {
            this.setState("disconnected", reason);
        }
        this.userDisconnecting = false;
    }

    // ---- internals ---------------------------------------------------------

    private setState(state: ConnectionState, reason?: string): void {
        this.state = state;
        this.listeners.onStateChange?.(state, reason);
    }

    private sendControl(msg: ControlMessage): void {
        if (this.ws === null || this.state !== "connected") {
            return;
        }
        this.ws.send(serialiseControlMessage(msg));
    }

    private cleanupAfterFailure(): void {
        if (this.ws !== null) {
            try {
                this.ws.close();
            } catch {
                // ignore — we're already in a failure path
            }
            this.ws = null;
        }
        this.helloOk = null;
        this.setState("disconnected");
    }

    /**
     * Wait for the very next text frame after sending `hello` and resolve
     * to the parsed `hello_ok`, or throw with the specific failure mode.
     *
     * Listeners attached here are removed before the steady-state
     * listeners take over.
     */
    private awaitHandshakeReply(ws: WebSocket): Promise<HelloOk> {
        return new Promise<HelloOk>((resolve, reject) => {
            const timer = window.setTimeout(() => {
                cleanup();
                reject(new HandshakeError({ kind: "timeout" }));
                this.cleanupAfterFailure();
            }, HANDSHAKE_TIMEOUT_MS);

            const onMessage = (ev: MessageEvent): void => {
                if (typeof ev.data !== "string") {
                    cleanup();
                    reject(
                        new HandshakeError({
                            kind: "protocol",
                            detail: "binary frame before hello_ok",
                        }),
                    );
                    this.cleanupAfterFailure();
                    return;
                }
                const msg = parseControlMessage(ev.data);
                if (msg === null) {
                    cleanup();
                    reject(
                        new HandshakeError({
                            kind: "protocol",
                            detail: "malformed JSON in handshake reply",
                        }),
                    );
                    this.cleanupAfterFailure();
                    return;
                }
                if (msg.type === "hello_ok") {
                    cleanup();
                    resolve(msg);
                    return;
                }
                if (msg.type === "hello_reject") {
                    cleanup();
                    reject(
                        new HandshakeError({ kind: "rejected", reason: msg.reason }),
                    );
                    this.cleanupAfterFailure();
                    return;
                }
                cleanup();
                reject(
                    new HandshakeError({
                        kind: "protocol",
                        detail: `unexpected message type during handshake: ${msg.type}`,
                    }),
                );
                this.cleanupAfterFailure();
            };

            const onClose = (ev: CloseEvent): void => {
                cleanup();
                reject(
                    new HandshakeError({
                        kind: "transport",
                        detail: `socket closed during handshake (code=${ev.code})`,
                    }),
                );
                this.cleanupAfterFailure();
            };

            const onError = (): void => {
                cleanup();
                reject(
                    new HandshakeError({
                        kind: "transport",
                        detail: "socket error during handshake",
                    }),
                );
                this.cleanupAfterFailure();
            };

            const cleanup = (): void => {
                window.clearTimeout(timer);
                ws.removeEventListener("message", onMessage);
                ws.removeEventListener("close", onClose);
                ws.removeEventListener("error", onError);
            };

            ws.addEventListener("message", onMessage);
            ws.addEventListener("close", onClose);
            ws.addEventListener("error", onError);
        });
    }

    /**
     * Wire up the post-handshake message handlers. From this point on all
     * incoming frames go to the listener callbacks.
     */
    private attachSteadyStateListeners(ws: WebSocket): void {
        ws.addEventListener("message", (ev) => {
            if (typeof ev.data === "string") {
                this.handleControlText(ev.data);
            } else if (ev.data instanceof ArrayBuffer) {
                this.handleAudioBinary(new Uint8Array(ev.data));
            }
        });

        ws.addEventListener("close", (ev) => {
            this.handleUnexpectedClose(`server closed (code=${ev.code})`);
        });

        ws.addEventListener("error", () => {
            this.listeners.onTransportError?.("websocket error");
            this.handleUnexpectedClose("websocket error");
        });
    }

    /**
     * Decide what to do when the steady-state socket closes by surprise.
     *
     * If the user asked for the disconnect (via `disconnect()`), the
     * close handler is just unwinding the same operation — fall through
     * to a final `disconnect` and stop. Otherwise, kick off the
     * reconnect loop: enter `reconnecting` state and schedule the first
     * retry per the backoff schedule.
     */
    private handleUnexpectedClose(reason: string): void {
        if (this.userDisconnecting) {
            return;
        }
        if (this.credentials === null || this.reconnectTimer !== null) {
            // Either we never reached steady state (no credentials to
            // retry with) or a reconnect is already in flight. In either
            // case the right thing is a final disconnect.
            this.disconnect(reason);
            return;
        }
        // Drop the dead socket but keep the credentials and listeners
        // alive across the gap.
        if (this.ws !== null) {
            try {
                this.ws.close();
            } catch {
                // already closing — fine
            }
            this.ws = null;
        }
        this.helloOk = null;
        this.reconnectAttempt = 0;
        this.setState("reconnecting", reason);
        this.scheduleNextReconnect();
    }

    /** Cancel any pending reconnect timer. */
    private cancelReconnect(): void {
        if (this.reconnectTimer !== null) {
            window.clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    /**
     * Arrange the next reconnect attempt according to
     * [`RECONNECT_BACKOFF_MS`]; if the schedule is exhausted, give up
     * and tear down for real.
     */
    private scheduleNextReconnect(): void {
        const delay = RECONNECT_BACKOFF_MS[this.reconnectAttempt];
        if (delay === undefined) {
            // Out of retries — surface a normal disconnect so the
            // channel screen returns the user to the lobby.
            this.disconnect("reconnect failed");
            return;
        }
        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = null;
            void this.attemptReconnect();
        }, delay);
    }

    /**
     * One reconnect attempt: open a fresh socket, run the handshake,
     * and either resume the session or schedule the next backoff slot.
     */
    private async attemptReconnect(): Promise<void> {
        if (this.userDisconnecting || this.credentials === null) return;
        const { url, secret, displayName } = this.credentials;

        const ws = new WebSocket(url);
        ws.binaryType = "arraybuffer";
        this.ws = ws;

        try {
            await waitForOpen(ws);
            ws.send(
                serialiseControlMessage({
                    type: "hello",
                    display_name: displayName,
                    secret,
                }),
            );
            const helloOk = await this.awaitHandshakeReply(ws);
            // `awaitHandshakeReply` calls `cleanupAfterFailure` on its
            // own error paths, which clears `this.ws`. On success it
            // does not — we keep the socket and proceed.
            if (this.userDisconnecting) {
                // User pulled the plug while we were handshaking. Drop
                // the brand-new connection and bail.
                try { ws.close(1000, "client disconnect"); } catch { /* ignore */ }
                this.ws = null;
                return;
            }
            this.helloOk = helloOk;
            this.attachSteadyStateListeners(ws);
            this.setState("connected");
            this.listeners.onReconnected?.(helloOk);
        } catch {
            // Either the open or the handshake failed. `attemptReconnect`
            // does not throw — it just retries until the schedule is
            // exhausted. The error has already been routed through
            // `cleanupAfterFailure` so `this.ws` is null.
            if (this.userDisconnecting) return;
            // `cleanupAfterFailure` set state to "disconnected"; restore
            // it to "reconnecting" before scheduling the next attempt
            // so the UI keeps showing the right thing.
            this.setState("reconnecting", "retrying handshake");
            this.reconnectAttempt += 1;
            this.scheduleNextReconnect();
        }
    }

    private handleControlText(text: string): void {
        const msg = parseControlMessage(text);
        if (msg === null) {
            // Malformed JSON from the server is a protocol bug; surface
            // but do not disconnect.
            this.listeners.onTransportError?.("malformed control message from server");
            return;
        }
        switch (msg.type) {
            case "floor_taken":
                this.listeners.onFloorTaken?.({
                    connection_id: msg.connection_id,
                    display_name: msg.display_name,
                });
                return;
            case "floor_released":
                this.listeners.onFloorReleased?.(msg.connection_id);
                return;
            case "member_joined":
                this.listeners.onMemberJoined?.({
                    connection_id: msg.connection_id,
                    display_name: msg.display_name,
                });
                return;
            case "member_left":
                this.listeners.onMemberLeft?.(msg.connection_id);
                return;
            case "floor_granted":
                this.listeners.onFloorGranted?.();
                return;
            case "floor_denied":
                this.listeners.onFloorDenied?.(msg.reason);
                return;
            case "error":
                this.listeners.onServerError?.(msg.code, msg.message);
                return;
            case "hello":
            case "hello_ok":
            case "hello_reject":
            case "request_floor":
            case "release_floor":
                // These are either client-only or handshake-only and
                // should not appear post-handshake. Ignore quietly.
                return;
        }
    }

    private handleAudioBinary(bytes: Uint8Array): void {
        let frame: AudioFrame;
        try {
            frame = decodeAudioFrame(bytes);
        } catch {
            // Server should never send malformed audio; a safe drop.
            return;
        }
        this.listeners.onAudio?.(frame);
    }
}

/** Resolve once the WebSocket reaches OPEN, or reject on early close/error. */
function waitForOpen(ws: WebSocket): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const cleanup = (): void => {
            ws.removeEventListener("open", onOpen);
            ws.removeEventListener("error", onError);
            ws.removeEventListener("close", onClose);
        };
        const onOpen = (): void => {
            cleanup();
            resolve();
        };
        const onError = (): void => {
            cleanup();
            reject(new Error("WebSocket error before open"));
        };
        const onClose = (ev: CloseEvent): void => {
            cleanup();
            reject(new Error(`WebSocket closed before open (code=${ev.code})`));
        };
        ws.addEventListener("open", onOpen);
        ws.addEventListener("error", onError);
        ws.addEventListener("close", onClose);
    });
}

function errorMessage(e: unknown): string {
    if (e instanceof Error) {
        return e.message;
    }
    return String(e);
}
